import { jsx as _jsx } from "react/jsx-runtime";
import { SolutionType, } from '@coveo/headless/ssr-commerce';
import { useContext, useCallback, useMemo, } from 'react';
import { useSyncMemoizedStore } from '../client-utils.js';
import { MissingEngineProviderError, UndefinedControllerError, } from '../errors.js';
import { capitalize, mapObject } from '../utils.js';
function isHydratedStateContext(ctx) {
    return 'engine' in ctx;
}
function buildControllerHook(singletonContext, key, controllerDefinition) {
    return () => {
        const ctx = useContext(singletonContext.get());
        if (ctx === null) {
            throw new MissingEngineProviderError();
        }
        const allSolutionTypes = Object.values(SolutionType);
        const supportedSolutionTypes = allSolutionTypes.filter((solutionType) => controllerDefinition[solutionType] === true);
        if (ctx.controllers[key] === undefined) {
            throw new UndefinedControllerError(key.toString(), supportedSolutionTypes);
        }
        const subscribe = useCallback((listener) => isHydratedStateContext(ctx)
            ? ctx.controllers[key].subscribe(listener)
            : () => { }, [ctx]);
        const getStaticState = useCallback(() => ctx.controllers[key].state, [ctx]);
        const state = useSyncMemoizedStore(subscribe, getStaticState);
        const methods = useMemo(() => {
            if (!isHydratedStateContext(ctx)) {
                return undefined;
            }
            const controller = ctx.controllers[key];
            const { state: _, subscribe: __, ...remainder } = controller;
            return mapObject(remainder, (member) => typeof member === 'function' ? member.bind(controller) : member);
        }, [ctx, key]);
        return { state, methods };
    };
}
export function buildControllerHooks(singletonContext, controllersMap) {
    return (controllersMap
        ? Object.fromEntries(Object.entries(controllersMap).map(([key, controllerDefinition]) => [
            `use${capitalize(key)}`,
            buildControllerHook(singletonContext, key, controllerDefinition),
        ]))
        : {});
}
export function buildEngineHook(singletonContext) {
    return () => {
        const ctx = useContext(singletonContext.get());
        if (ctx === null) {
            throw new MissingEngineProviderError();
        }
        return isHydratedStateContext(ctx) ? ctx.engine : undefined;
    };
}
export function buildStaticStateProvider(singletonContext, solutionType) {
    return ({ controllers, children, }) => {
        const { Provider } = singletonContext.get();
        return _jsx(Provider, { value: { controllers, solutionType }, children: children });
    };
}
export function buildHydratedStateProvider(singletonContext, solutionType) {
    return ({ engine, controllers, children, }) => {
        const { Provider } = singletonContext.get();
        return (_jsx(Provider, { value: { engine, controllers, solutionType }, children: children }));
    };
}
//# sourceMappingURL=common.js.map